import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { ChatPreview } from "@web/components/ChatPreview";
import { LoadingSpinner } from "@web/components/LoadingSpinner";
import { Navbar } from "@web/components/Navbar";
import { UnAuthedPage } from "@web/components/UnAuthedPage";
import { useInfiniteScroll } from "@web/hooks/use-infinite-scroll";
import { useChatPreviewQuery } from "@web/route-services/chat/use-chat-preview-query";
import { useAppContext } from "@web/route-services/root/AppContext";
import { AnimatePresence, motion } from "motion/react";
import { useCallback } from "react";
import { FaArrowDown } from "react-icons/fa";

export const Route = createFileRoute("/recent-chats")({
  component: RecentChats
});

function RecentChats() {
  const { session, isDesktopScreen } = useAppContext();
  const { data: chatPreviewPages, fetchNextPage, isPending, isError, isSuccess } = useChatPreviewQuery();
  const nav = useNavigate();

  const chatPreviews = chatPreviewPages?.pages.flatMap((page) => page) || [];
  const scrolledToBottomHandler = useCallback(() => {
    fetchNextPage();
  }, [fetchNextPage]);

  const { topSentinelRef, bottomSentinelRef } = useInfiniteScroll({ items: chatPreviews, scrolledToBottomHandler });

  if (!session) return <UnAuthedPage />;

  /**
   * This is a mobile only route, we redirect to the chat page if we're on desktop.
   */
  if (isDesktopScreen) {
    nav({ to: "/chat" });
    return null;
  }

  return (
    <>
      <div className="text-tx-primary mt-3 px-6 text-3xl font-semibold">Recent Chats</div>
      <div className="scroll-chat-sidebar flex grow flex-col overflow-x-hidden overflow-y-scroll px-2 py-4">
        <div ref={topSentinelRef} className="w-full" />

        {isPending && <LoadingSpinner className="m-auto" />}

        {isError && <p className="text-err m-auto select-none text-center text-xl leading-9">Failed to load chats.</p>}

        {isSuccess && chatPreviews.length === 0 && (
          <div className="bg-background flex h-screen w-screen items-center justify-center overflow-auto">
            <AnimatePresence mode="wait">
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="flex h-full w-full justify-center pb-10"
              >
                <div className="text-center">
                  <p className="text-tx-tertiary select-none text-center text-xl leading-9">
                    You don&apos;t have any chats. <br />
                    Start a chat by going to{" "}
                    <Link to="/" className="text-tx-secondary hover:underline">
                      Explore
                    </Link>
                    <br />
                    and clicking on a card. <br />
                    (づ ◕‿◕ )づ
                  </p>
                  <div className="text-action-primary fixed bottom-16 left-10 animate-bounce">
                    <FaArrowDown className="text-6xl" />
                  </div>
                </div>
              </motion.div>
            </AnimatePresence>
          </div>
        )}
        {!!chatPreviews.length && (
          <div className="w-full pb-12">
            {chatPreviews?.map((preview, idx, arr) => (
              <ChatPreview key={preview.chatID} preview={preview} className={`${idx < arr.length - 1 ? "mb-4" : ""}`} />
            ))}
          </div>
        )}
        <div ref={bottomSentinelRef} className="w-full" />

        <Navbar />
      </div>
    </>
  );
}
